import { useEffect, useId, useMemo, useState } from 'react'
import { ChevronDown, Search, Sliders } from 'lucide-react'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import TextField from '../../components/TextField'
import Select from '../../components/Select'
import { useToast } from '../../components/toastContext'
import { queryDataSourceProperties, saveProfileMapping, sourceName } from './settingsService'
import { advancedStrings as S, common as C } from './strings'
import { serverDetail, serverText } from '../../lib/i18n'
import { directoryAttributes, directoryMappingPayload, initialDirectoryMappings, isEntra } from './directoryMapping'

/**
 * Which directory attribute fills each profile field.
 *
 * Two panels: the EXPLORER reads one account's attributes so the administrator can see what
 * the directory actually holds, and the MAPPING picks an attribute per field. Both are drawn
 * from the panel's own tokens — the first version styled itself inline with token names this
 * app does not define, so its fallbacks painted light boxes into the dark theme, and its
 * search button was handed `icon`, which makes a Button an icon-only square: the label
 * spilled out past the edge of the dialog.
 */
export default function DataSourceMappingModal({ source, onClose, onSaved }) {
  const toast = useToast()
  const exploreId = useId()
  const [sampleUsername, setSampleUsername] = useState('')
  const [exploring, setExploring] = useState(false)
  const [saving, setSaving] = useState(false)
  const [discoveredProps, setDiscoveredProps] = useState([])
  const [propertyPairs, setPropertyPairs] = useState([])
  const [showPreview, setShowPreview] = useState(false)
  const [mappings, setMappings] = useState({})

  // Initialize mappings from source or defaults
  useEffect(() => {
    if (!source) return
    setMappings(initialDirectoryMappings(source))
    setDiscoveredProps([])
    setPropertyPairs([])
    setShowPreview(false)
    setSampleUsername(source.username || '')
  }, [source])

  /* The attributes appear in the panel the moment they arrive, so success says nothing
     more; only a refusal needs words. */
  const exploreAttributes = async (event) => {
    event?.preventDefault()
    if (!source) return
    setExploring(true)
    const result = await queryDataSourceProperties(source.id, sampleUsername)
    setExploring(false)

    if (result.ok) {
      setDiscoveredProps(result.properties || [])
      setPropertyPairs(result.propertyPairs || [])
      setShowPreview(true)
    } else {
      toast.error(serverDetail(result.message) || C.saveFailed)
    }
  }

  const allAvailableAttributes = useMemo(() => {
    const set = new Set([...(isEntra(source) ? [] : discoveredProps), ...directoryAttributes(source)])
    return Array.from(set).sort()
  }, [discoveredProps, source])

  const selectOptions = useMemo(() => {
    const list = [{ value: 'Unbound', label: S.sources.notMapped }]
    for (const attr of allAvailableAttributes) {
      list.push({ value: attr, label: attr })
    }
    return list
  }, [allAvailableAttributes])

  const handleFieldChange = (fieldName, value) => {
    setMappings((prev) => ({ ...prev, [fieldName]: value }))
  }

  const handleSave = async () => {
    if (!source) return
    setSaving(true)
    const payload = directoryMappingPayload(mappings)

    const outcome = await saveProfileMapping(source.id, payload)
    setSaving(false)

    if (outcome.ok) {
      toast.success(serverText(outcome.message) || S.sources.mappingSaved)
      onSaved?.()
      onClose()
    } else {
      toast.error(serverDetail(outcome.message) || C.saveFailed)
    }
  }

  // Profile fields list
  const profileFieldsList = useMemo(
    () => [
      { key: 'EnglishName', label: S.profile.fields[0].label || 'English Name' },
      { key: 'ArabicName', label: S.profile.fields[1].label || 'Arabic Name' },
      { key: 'Email', label: S.profile.fields[3].label || 'Email' },
      { key: 'DepartmentId', label: S.profile.fields[6].label || 'Department' },
      { key: 'Phone', label: S.profile.fields[4].label || 'Telephone' },
      { key: 'Mobile', label: S.profile.fields[5].label || 'Mobile' },
      { key: 'EmployeeId', label: S.profile.fields[7].label || 'Employee ID' },
      { key: 'Username', label: S.profile.fields[8].label || 'Username' },
      { key: 'Resigned', label: S.sources.resignedField },
      { key: 'Attribute1', label: S.profile.customAttributes + ' 1' },
      { key: 'Attribute2', label: S.profile.customAttributes + ' 2' },
      { key: 'Attribute3', label: S.profile.customAttributes + ' 3' },
      { key: 'Attribute4', label: S.profile.customAttributes + ' 4' },
      { key: 'Attribute5', label: S.profile.customAttributes + ' 5' },
    ],
    [],
  )

  const title = `${S.sources.profileMapping} - ${sourceName(source)}`

  return (
    <Modal
      open={Boolean(source)}
      onClose={onClose}
      title={title}
      icon={Sliders}
      size="xl"
      footer={
        <>
          <Button busy={saving} onClick={handleSave}>
            {S.sources.saveMapping}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {C.reset}
          </Button>
        </>
      }
    >
      <div className="dsmap">
        <section className="dsmap__explore" aria-labelledby={exploreId}>
          <h3 className="dsmap__heading" id={exploreId}>
            <bdi>{S.sources.queryProperties}</bdi>
          </h3>

          <form className="dsmap__query" onSubmit={exploreAttributes}>
            <TextField
              value={sampleUsername}
              onChange={setSampleUsername}
              placeholder={S.sources.usernameToExplore}
              aria-label={S.sources.usernameToExplore}
              dir="ltr"
            />
            <Button type="submit" variant="soft" busy={exploring}>
              <Search size={16} strokeWidth={2} aria-hidden="true" />
              {S.sources.exploreButton}
            </Button>
          </form>

          {propertyPairs.length > 0 && (
            <div className="dsmap__found">
              <button
                type="button"
                className={`dsmap__toggle${showPreview ? ' is-open' : ''}`}
                aria-expanded={showPreview}
                onClick={() => setShowPreview(!showPreview)}
              >
                <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
                <bdi>{S.sources.samplePropertiesPreview}</bdi>
                <span className="dsmap__count">{propertyPairs.length}</span>
              </button>

              {showPreview && (
                <div className="dsmap__props">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">
                          <bdi>{S.sources.sourceAttribute}</bdi>
                        </th>
                        <th scope="col">
                          <bdi>{S.sources.value}</bdi>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {propertyPairs.map((pair) => (
                        <tr key={pair.Name}>
                          <th scope="row" dir="ltr">
                            {pair.Name}
                          </th>
                          <td>
                            <bdi>{pair.Value}</bdi>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="dsmap__map">
          <div className="dsmap__map-head" aria-hidden="true">
            <bdi>{S.sources.builtInField}</bdi>
            <bdi>{S.sources.sourceAttribute}</bdi>
          </div>

          <ul className="dsmap__rows">
            {profileFieldsList.map((field) => (
              <li key={field.key} className="dsmap__row">
                <span className="dsmap__field">
                  <bdi className="dsmap__label">{field.label}</bdi>
                  {/* Latin, but on the field's own side of the row. */}
                  <span className="dsmap__key">
                    <bdi dir="ltr">{field.key}</bdi>
                  </span>
                </span>
                <Select
                  options={selectOptions}
                  value={mappings[field.key] ?? 'Unbound'}
                  disabled={field.key === 'Username' || field.key === 'Resigned'}
                  onChange={(val) => handleFieldChange(field.key, val)}
                  placeholder={S.sources.notMapped}
                  aria-label={field.label}
                  searchable
                />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Modal>
  )
}
